import {
  DetailType,
  EventStatusId,
  IncidentAttributeId,
  IncidentId,
  Property,
  SportId,
  SportIncident,
  SportStatus,
} from '@collector/sportsapi-client-legacy'

import { getDetailsPanelSportConfiguration } from '../../DetailsPanelSportConfigurationFactory'
import {
  DetailButtonsConfiguration,
  DetailInputConfiguration,
  DetailsComponentConfiguration,
  DetailSelectConfiguration,
  IncidentDetailsConfiguration,
  SportAttributesConfiguration,
  SportDetailsConfiguration,
} from './types'

export function isDetailButtonsConfiguration(
  config: unknown,
): config is DetailButtonsConfiguration<IncidentAttributeId> {
  return (
    (config as DetailButtonsConfiguration<IncidentAttributeId>).type ===
      DetailType.Button &&
    (config as DetailButtonsConfiguration<IncidentAttributeId>).attributes !==
      undefined
  )
}

export function isDetailInputConfiguration(
  config: unknown,
): config is DetailInputConfiguration<IncidentAttributeId> {
  return (
    (config as DetailInputConfiguration<IncidentAttributeId>).type ===
    DetailType.Input
  )
}

export function isDetailSelectConfiguration(
  config: unknown,
): config is DetailSelectConfiguration<IncidentAttributeId> {
  return (
    (config as DetailSelectConfiguration<IncidentAttributeId>).type ===
    DetailType.Select
  )
}

export function isDetailsComponentConfiguration(
  config: IncidentDetailsConfiguration<IncidentAttributeId>,
): config is DetailsComponentConfiguration {
  return (config as DetailsComponentConfiguration).component !== undefined
}

export function mapIncidents(
  fn: (sportId: SportId) => IncidentId[] | EventStatusId[],
  list: { [key: number]: SportIncident | SportStatus | undefined },
  sportId: number,
): {
  name: string
  value: string | number
}[] {
  const incidentsList = fn(sportId)

  return Object.values(list)
    .filter((incident) =>
      incidentsList.some((id) => Number(id) === incident?.id),
    )
    .map((incident) => {
      return {
        name: incident?.name ?? '',
        value: incident?.id ?? '',
      }
    })
    .sort(
      (a, b) =>
        incidentsList.indexOf(a?.value as never) -
        incidentsList.indexOf(b?.value as never),
    )
}

export function shouldShowCurrentPeriodResult(sportId: SportId): boolean {
  return (
    getDetailsPanelSportConfiguration(sportId).showCurrentPeriodResult ?? false
  )
}

export function shouldReversePagination(sportId: SportId): boolean {
  return getDetailsPanelSportConfiguration(sportId).reversePagination ?? false
}

export function getHideIncidentsFilterValues(sportId: SportId): IncidentId[] {
  return getDetailsPanelSportConfiguration(sportId).filters.hideIncidents
}

export function getParticipantIncidentsFilterValues(
  sportId: SportId,
): IncidentId[] {
  return getDetailsPanelSportConfiguration(sportId).filters.participantIncidents
}

export function getSecondParticipantIncidents(sportId: SportId): IncidentId[] {
  return (getDetailsPanelSportConfiguration(sportId)
    ?.secondParticipantIncidents ?? []) as IncidentId[]
}

export function getDetailsIncidentsFilterValues(
  sportId: SportId,
): IncidentId[] {
  return getDetailsPanelSportConfiguration(sportId).filters.detailsIncidents
}

export function getSelectedOnStartDetailsIncidents(
  sportId: SportId,
): IncidentId[] {
  return (getDetailsPanelSportConfiguration(sportId).filters
    .selectedOnStartDetailsIncidents ?? []) as IncidentId[]
}

export function getManualAddIncidents(sportId: SportId): IncidentId[] {
  return (getDetailsPanelSportConfiguration(sportId).manualAddIncidentModal
    .incidents ?? []) as IncidentId[]
}

export function getManualAddSubstitutionIncidents(
  sportId: SportId,
): IncidentId[] {
  return (getDetailsPanelSportConfiguration(sportId).manualAddIncidentModal
    .substitutionIncidents ?? []) as IncidentId[]
}

export function getManualAddIncidentModalStatuses(
  sportId: SportId,
): EventStatusId[] {
  return getDetailsPanelSportConfiguration(sportId).manualAddIncidentModal
    .statuses
}

export function getDetailsConfiguration(
  sportId: SportId,
): SportDetailsConfiguration<IncidentId, IncidentAttributeId> {
  return getDetailsPanelSportConfiguration(sportId).detailsConfiguration
}

export function getAttributesConfiguration(
  sportId: SportId,
): SportAttributesConfiguration<IncidentId, IncidentAttributeId> {
  return getDetailsPanelSportConfiguration(sportId).attributesConfiguration
}

export function getPropertyIndex(
  properties: Property[] | undefined,
  propertyName: string | undefined,
): number {
  const index = properties?.findIndex(
    (property) =>
      property.short_name.toLowerCase() === propertyName?.toLowerCase(),
  )

  return !index || index === -1 ? 0 : index
}

export function enforceMinMax(
  inputValues: Property[] | undefined,
  incidentProperties: Property[] | undefined,
  config: DetailInputConfiguration<IncidentAttributeId>,
): void {
  if (inputValues) {
    const selectedPropertyIndex = getPropertyIndex(
      incidentProperties,
      config.name,
    )

    if (inputValues[selectedPropertyIndex].value === '') {
      return
    }

    const value = String(inputValues[selectedPropertyIndex].value)
    // Allow a leading minus sign for negative numbers, but remove any other non-digit characters.
    const sanitizedValue = value.replace(/(?!^)-|[^0-9-]/g, '')
    inputValues[selectedPropertyIndex].value = sanitizedValue

    if (sanitizedValue === '-') {
      return
    }

    if (
      Number(inputValues[selectedPropertyIndex].value) ||
      Number(inputValues[selectedPropertyIndex].value) === 0
    ) {
      if (config.min !== undefined) {
        inputValues[selectedPropertyIndex].value = Math.max(
          Number(inputValues[selectedPropertyIndex].value),
          config.min,
        )
      }

      if (config.max !== undefined) {
        inputValues[selectedPropertyIndex].value = Math.min(
          Number(inputValues[selectedPropertyIndex].value),
          config.max,
        )
      }
    } else {
      inputValues[selectedPropertyIndex].value = null
    }
  }
}
